import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle, RefreshCw, Scale, FileText, Ban, Gavel, UserX, Lock, Clock, Globe, Smartphone, CreditCard, Users, MessageSquare, Zap } from "lucide-react";
import eventBus from "../../../utils/eventBus";

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 2024";
  const effectiveDate = "December 2024";

  useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      eventBus.emit("fullscreen:exit");
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Terms of Service</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl border border-slate-700 p-6 md:p-10 space-y-8">
          
          {/* Warning Banner */}
          <div className="bg-red-500/20 border border-red-500/50 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-bold text-red-400 mb-2">IMPORTANT LEGAL NOTICE - PLEASE READ CAREFULLY</h2>
                <p className="text-red-200 text-sm leading-relaxed">
                  By using Arthereum Wallet, you acknowledge that this is an <strong>experimental application</strong> involving 
                  cryptocurrency and digital tokens. You may <strong>lose all your funds</strong>. The tokens have <strong>no guaranteed 
                  financial value</strong>. By continuing to use this service, you accept all risks and waive your right to file 
                  lawsuits or class action claims against the developers, operators, affiliates, and all associated parties.
                  This agreement contains binding arbitration provisions and a waiver of class action rights.
                </p>
              </div>
            </div>
          </div>

          {/* Meta Info */}
          <div className="text-slate-400 text-sm space-y-1">
            <p><strong>Last Updated:</strong> {lastUpdated}</p>
            <p><strong>Effective Date:</strong> {effectiveDate}</p>
          </div>

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">1</span>
              Acceptance of Terms
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                Welcome to Arthereum Wallet ("App", "Service", "Platform", "we", "us", "our"). These Terms of Service 
                ("Terms", "Agreement") constitute a legally binding agreement between you ("User", "you", "your") 
                and the operators of Arthereum Wallet, including all developers, affiliates, partners, contractors, 
                and any individuals or entities associated with the creation, maintenance, or operation of this Service.
              </p>
              <p>
                <strong>1.1 Binding Agreement:</strong> By accessing, downloading, installing, registering for, or using Arthereum Wallet 
                in any manner, you acknowledge that you have read, understood, and agree to be bound by these Terms in their 
                entirety. This includes all future amendments or modifications. If you do not agree with any part of these Terms, 
                you must immediately cease using the Service and delete any associated applications or data.
              </p>
              <p>
                <strong>1.2 Capacity to Contract:</strong> You represent and warrant that you have the legal capacity and authority 
                to enter into this binding agreement. If you are entering this agreement on behalf of an organization, you represent 
                that you have the authority to bind that organization to these Terms.
              </p>
              <p>
                <strong>1.3 Modifications:</strong> We reserve the right to modify, amend, or update these Terms at any time, at our 
                sole discretion, without prior notice to you. Your continued use of the Service after any modifications constitutes 
                your acceptance of the updated Terms. It is your responsibility to review these Terms periodically.
              </p>
              <p>
                <strong>1.4 Electronic Agreement:</strong> You consent to receive this agreement and any amendments electronically. 
                Your use of the Service constitutes your electronic signature and acceptance of these Terms.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">2</span>
              Eligibility Requirements
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>To use Arthereum Wallet, you must meet ALL of the following requirements:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Be at least 18 years of age, or the age of legal majority in your jurisdiction, whichever is higher</li>
                <li>Have the full legal capacity to enter into a binding contractual agreement</li>
                <li>Not be prohibited from using cryptocurrency services under any applicable laws, regulations, or sanctions</li>
                <li>Have a valid and verified Pi Network account for authentication purposes</li>
                <li>Not be located in, or a citizen or resident of, any jurisdiction where cryptocurrency services are prohibited or restricted</li>
                <li>Not be on any government sanctions list or associated with any sanctioned entities</li>
                <li>Not have previously been banned or suspended from using this Service</li>
                <li>Have the technical capability and understanding to use cryptocurrency applications</li>
              </ul>
              <p>
                <strong>2.1 Verification:</strong> We reserve the right to verify your eligibility at any time. You agree to provide 
                accurate, current, and complete information and to update such information as necessary.
              </p>
              <p>
                <strong>2.2 Termination for Ineligibility:</strong> If we discover that you do not meet these eligibility requirements, 
                we reserve the right to immediately terminate your access to the Service, suspend your account, and forfeit any 
                tokens, balances, or rewards associated with your account, without any liability to you.
              </p>
            </div>
          </section>

          {/* Section 3 - CRITICAL */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center text-red-400 text-sm font-bold">3</span>
              <span className="text-red-400">ARTHEREUM TOKEN (ATH) DISCLAIMER</span>
            </h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 ml-11">
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p className="text-red-300 font-semibold">
                  YOU EXPRESSLY ACKNOWLEDGE, UNDERSTAND, AND AGREE TO THE FOLLOWING CRITICAL DISCLAIMERS:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>NO FINANCIAL VALUE:</strong> Arthereum (ATH) tokens have ABSOLUTELY NO guaranteed, intrinsic, 
                    assured, or promised financial value whatsoever. The token's price, if any exists at any point, is purely 
                    speculative, volatile, and may be zero at any time. Any perceived value is entirely subjective and not 
                    endorsed or guaranteed by us.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>NOT AN INVESTMENT:</strong> ATH tokens are categorically NOT securities, investments, financial 
                    instruments, commodities, or any form of investment opportunity. Mining, earning, holding, trading, or 
                    transacting with ATH tokens does NOT constitute an investment and should NOT be treated as such.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>NO PROFIT EXPECTATION:</strong> You should NOT expect any financial gains, returns, profits, 
                    dividends, income, appreciation, or any form of monetary benefit from mining, holding, or transacting with 
                    ATH tokens. We make NO representations about future value.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>ENTERTAINMENT & UTILITY ONLY:</strong> The token mining, grant features, and all related 
                    functionalities are provided EXCLUSIVELY for entertainment, gamification, educational, and utility purposes 
                    within the application ecosystem. While ATH tokens may potentially acquire market value in the future, 
                    this is NOT guaranteed, and you should NOT expect, rely upon, or count on any financial returns, profits, 
                    or appreciation. Any future value is purely speculative and should not influence your decision to use the Service.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>TOTAL LOSS RISK:</strong> You explicitly acknowledge and accept that you may lose the entirety 
                    of any perceived value associated with ATH tokens. This includes complete loss of all tokens in your account. 
                    This risk is inherent to the Service and is fully accepted by your use of it.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>NO REDEMPTION GUARANTEE:</strong> There is no guarantee that ATH tokens can be exchanged, 
                    redeemed, sold, traded, or converted into any other currency, cryptocurrency, goods, services, or value 
                    at any time, now or in the future.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span><strong>NO REGULATORY APPROVAL:</strong> ATH tokens have not been approved, endorsed, or reviewed by 
                    any regulatory authority in any jurisdiction. We do not claim compliance with any securities laws.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 - STAKING */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center text-orange-400 text-sm font-bold">4</span>
              <RefreshCw className="w-6 h-6 text-orange-400" />
              <span className="text-orange-400">STAKING - PI CONTRIBUTION & CONVERSION</span>
            </h2>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-5 ml-11">
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p className="text-orange-300 font-bold text-lg">
                  ⚠️ CRITICAL NOTICE - PERMANENT ONE-WAY STAKING ⚠️
                </p>
                <p>
                  The Arthereum Wallet features a unique <strong>Staking System</strong> that enables users to stake 
                  their Pi tokens in exchange for enhanced mining capabilities. This staking mechanism operates on a <strong>permanent 
                  contribution model</strong> which is fundamentally different from traditional staking mechanisms where tokens are 
                  returned. By participating in staking, you explicitly acknowledge and irrevocably agree to the following:
                </p>
                <div className="bg-slate-900/50 rounded-xl p-4 space-y-4 border border-orange-500/20">
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-orange-300">PERMANENT STAKING - PI IS CONVERTED</h4>
                      <p className="text-sm">When you stake Pi in Arthereum Wallet, your Pi 
                      is <strong>permanently converted</strong> into mining boost credits. This staking conversion is by design, 
                      irreversible, and cannot be undone under any circumstances. The Pi you stake is permanently 
                      transferred and consumed by the ecosystem to power your enhanced mining capabilities.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-orange-300">NON-REVERSIBLE STAKING - NO RETURN OF PI</h4>
                      <p className="text-sm">At the conclusion of your staking period, or at any point thereafter, you will 
                      <strong> NOT receive your staked Pi back</strong>. The Pi is completely and permanently consumed 
                      as part of the ecosystem's value redistribution mechanism. In exchange, you receive temporary enhanced 
                      mining boost capabilities for ATH tokens during the active staking period only.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-orange-300">STAKING VALUE EXCHANGE</h4>
                      <p className="text-sm">By staking Pi, you are participating in a value exchange where your staked Pi is 
                      permanently converted into temporary mining boost capabilities. This staking mechanism helps maintain ecosystem 
                      balance through controlled supply management. Once your staking period expires, both your staked Pi 
                      and the associated boost cease to exist. This is the intentional and designed functionality of the staking system.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-orange-300">STAKE ONLY WHAT YOU CAN AFFORD TO PERMANENTLY LOSE</h4>
                      <p className="text-sm">You should only stake Pi that you are fully prepared to permanently lose 
                      and never recover. Do not stake Pi that you cannot afford to lose, that you may need in the future, 
                      or that you expect to receive back at any point. Treat any Pi staking as a permanent, irreversible 
                      contribution to the ecosystem.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-orange-300">STAKING IS ABSOLUTELY NON-REFUNDABLE</h4>
                      <p className="text-sm">Once Pi is staked, the transaction is absolutely 
                      final and cannot be reversed, cancelled, refunded, or restored under any circumstances whatsoever. 
                      The development team, operators, and all associated parties cannot and will not restore, return, or 
                      compensate for staked Pi. No exceptions will be made regardless of circumstances, including but 
                      not limited to claims of user error, technical issues, misunderstanding, or change of mind.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <RefreshCw className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-orange-300">STAKING SUPPORTS ECOSYSTEM SUSTAINABILITY</h4>
                      <p className="text-sm">The permanent staking mechanism serves to maintain long-term ecosystem 
                      sustainability by creating deflationary pressure and controlled supply management. Users who stake Pi 
                      support the ecosystem's health while receiving temporary enhanced mining capabilities. This staking model ensures 
                      that value is redistributed to active participants rather than accumulated indefinitely.</p>
                    </div>
                  </div>
                </div>
                <p className="text-orange-200 font-semibold text-sm italic">
                  BY INITIATING A STAKE, YOU IRREVOCABLY CONFIRM THAT YOU FULLY UNDERSTAND, ACCEPT, 
                  AND AGREE THAT YOUR STAKED PI WILL BE PERMANENTLY CONVERTED AND CONSUMED BY THE ECOSYSTEM, AND YOU WILL 
                  NOT RECEIVE IT BACK UNDER ANY CIRCUMSTANCES.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 - Service Features */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">5</span>
              Service Features & Limitations
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-4 pl-11">
              <p>Arthereum Wallet provides the following features, each subject to specific terms, limitations, and disclaimers:</p>
              
              <div className="space-y-4">
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.1 Mining & Grants</h4>
                  <p className="text-sm">Users may claim ATH token grants at periodic intervals determined by our algorithms. 
                  Grant amounts are variable, algorithmic, and subject to change without notice. Grants are NOT guaranteed, 
                  may be modified, reduced, suspended, or terminated at any time at our sole discretion without prior notice 
                  or compensation. Mining rates, frequencies, multipliers, and eligibility criteria may change at any time. 
                  Past performance or grant amounts do not guarantee future grants.</p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.2 Contests, Rewards & Competitions</h4>
                  <p className="text-sm">Daily contests and reward systems are provided for entertainment purposes only. 
                  All prizes are awarded at our sole and absolute discretion. There is NO guarantee of winning, no implied 
                  odds, and contest rules, prizes, eligibility, and participation requirements may change at any time without 
                  notice. We reserve the right to disqualify participants, modify results, or cancel contests at any time. 
                  Contest participation does not constitute gambling where prohibited by law; users are responsible for 
                  compliance with local laws.</p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.3 Lucky Spin & Chance-Based Features</h4>
                  <p className="text-sm">The Lucky Spin and similar features are games of chance provided purely for 
                  entertainment. Outcomes are randomized and no guaranteed rewards are promised. All rewards are at our 
                  discretion. This feature is NOT gambling as no real money is wagered and tokens have no guaranteed value. 
                  We reserve the right to modify odds, rewards, frequency, or eliminate this feature entirely.</p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.4 Pro Subscription & Premium Features</h4>
                  <p className="text-sm">Premium features are available through Pi payment. ALL subscriptions and payments 
                  are <strong>absolutely non-refundable</strong> under any circumstances, including but not limited to 
                  service changes, feature modifications, account termination, user dissatisfaction, or service discontinuation. 
                  Benefits, features, pricing, and terms may be modified at any time. Pro status does NOT guarantee any 
                  financial returns, token value appreciation, or specific outcomes. Subscription fees are consumed upon payment.</p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.5 Cryptocurrency Transfers & Transactions</h4>
                  <p className="text-sm">Users may send and receive cryptocurrency within the platform. ALL transactions 
                  are <strong>final, irreversible, and non-refundable</strong>. You are solely and completely responsible 
                  for ensuring the accuracy of recipient information, amounts, and transaction details. We are NOT liable 
                  for funds lost due to user error, incorrect addresses, typos, fraud, hacking, or any other cause. 
                  Blockchain transactions cannot be reversed. Double-check all transaction details before confirming.</p>
                </div>
                
                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.6 Referral & Invitation Program</h4>
                  <p className="text-sm">Referral bonuses and rewards are provided entirely at our discretion and may be 
                  modified, reduced, or terminated at any time without notice. Fraudulent referral activity, including but 
                  not limited to creating fake accounts, self-referrals, incentivized referrals outside our program, or 
                  any manipulation, will result in immediate account termination, forfeiture of ALL tokens and balances, 
                  and potential legal action. We reserve the right to define what constitutes fraudulent activity.</p>
                </div>

                <div className="bg-slate-700/30 rounded-xl p-4">
                  <h4 className="font-bold text-purple-300 mb-2">5.7 Advertising & Third-Party Content</h4>
                  <p className="text-sm">The Service may display advertisements from third-party networks. We are not 
                  responsible for the content, accuracy, or availability of third-party advertisements. Clicking on ads 
                  or interacting with third-party content is at your own risk. We do not endorse any advertised products 
                  or services.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 - Risk Acknowledgment */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">6</span>
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Comprehensive Assumption of Risk
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>By using Arthereum Wallet, you expressly acknowledge, understand, and unconditionally assume the following risks:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Extreme Volatility Risk:</strong> Cryptocurrency values are extremely volatile, unpredictable, and may drop to zero instantly. Price movements can be sudden and severe.</li>
                <li><strong>Technical & Software Risk:</strong> Software bugs, vulnerabilities, hacks, exploits, smart contract failures, or technical malfunctions may result in complete and permanent loss of funds.</li>
                <li><strong>Cybersecurity Risk:</strong> Despite security measures, no system is immune to hacking, phishing, malware, or other cyber attacks. Your funds may be stolen or lost.</li>
                <li><strong>Regulatory & Legal Risk:</strong> Changes in laws, regulations, or legal interpretations may affect the legality, functionality, or availability of cryptocurrency services in your jurisdiction.</li>
                <li><strong>Operational & Business Risk:</strong> The Service may be discontinued, suspended, or significantly modified at any time for any reason, including business, legal, or technical reasons.</li>
                <li><strong>Third-Party Risk:</strong> Third-party services (Pi Network, payment processors, hosting providers, etc.) may fail, change terms, or become unavailable, affecting your ability to use the Service.</li>
                <li><strong>Complete Loss Risk:</strong> You may lose ALL funds deposited, staked, earned, or held through the Service. This includes complete and permanent loss of all assets.</li>
                <li><strong>No Insurance:</strong> Funds held in this Service are NOT insured by any government agency, private insurance company, or any other entity. There is NO deposit protection.</li>
                <li><strong>Market Manipulation Risk:</strong> Cryptocurrency markets may be subject to manipulation, wash trading, or other fraudulent activities that may affect token values.</li>
                <li><strong>Liquidity Risk:</strong> There may be no market or liquidity for tokens, making them impossible to sell or exchange.</li>
                <li><strong>Tax & Compliance Risk:</strong> You are solely responsible for determining and paying any taxes or fees arising from your use of the Service.</li>
                <li><strong>Irreversibility Risk:</strong> Cryptocurrency transactions are generally irreversible. Mistakes cannot be corrected.</li>
              </ul>
              <p className="font-semibold text-yellow-300 mt-4">
                YOU ACKNOWLEDGE THAT YOU ARE SOLELY RESPONSIBLE FOR CONDUCTING YOUR OWN DUE DILIGENCE, RESEARCH, AND 
                RISK ASSESSMENT. YOU ARE USING THIS SERVICE ENTIRELY AT YOUR OWN RISK. WE PROVIDE NO ADVICE, 
                RECOMMENDATIONS, OR GUARANTEES OF ANY KIND.
              </p>
            </div>
          </section>

          {/* Section 7 - ARBITRATION */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center text-red-400 text-sm font-bold">7</span>
              <Gavel className="w-6 h-6 text-red-400" />
              <span className="text-red-400">BINDING ARBITRATION & DISPUTE RESOLUTION</span>
            </h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 ml-11">
              <div className="text-slate-300 leading-relaxed space-y-4">
                <p className="text-red-300 font-bold">
                  PLEASE READ THIS SECTION VERY CAREFULLY. IT SIGNIFICANTLY AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR 
                  RIGHT TO FILE A LAWSUIT IN COURT AND TO HAVE A JURY TRIAL.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.1 Mandatory Binding Arbitration</h4>
                    <p className="text-sm">ANY and ALL disputes, controversies, claims, or causes of action arising out of, 
                    relating to, or in connection with these Terms, the Service, your use of the Service, any transactions, 
                    any tokens, or the breach, termination, enforcement, interpretation, or validity thereof, shall be 
                    EXCLUSIVELY and FINALLY settled by binding arbitration. This includes disputes regarding the validity, 
                    scope, or enforceability of this arbitration clause itself. The arbitration shall be conducted by a 
                    single neutral arbitrator in accordance with the arbitration rules of a recognized arbitration institution. 
                    The seat and language of arbitration shall be determined by us. The arbitrator's decision shall be final 
                    and binding, with no right of appeal.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.2 Class Action & Collective Action Waiver</h4>
                    <p className="text-sm">YOU EXPLICITLY AND IRREVOCABLY AGREE THAT ANY AND ALL CLAIMS WILL BE BROUGHT 
                    SOLELY IN YOUR INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF, CLASS MEMBER, OR PARTICIPANT IN ANY 
                    PURPORTED CLASS ACTION, COLLECTIVE ACTION, REPRESENTATIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, 
                    OR ANY SIMILAR PROCEEDING. You expressly and permanently waive any right to participate in a class 
                    action, class-wide arbitration, or any consolidated or representative proceedings. The arbitrator 
                    may not consolidate claims or preside over any form of class or representative proceeding.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.3 Waiver of Jury Trial</h4>
                    <p className="text-sm">YOU HEREBY KNOWINGLY, VOLUNTARILY, AND IRREVOCABLY WAIVE YOUR RIGHT TO A TRIAL 
                    BY JURY in any dispute, controversy, or claim arising out of or relating to these Terms, the Service, 
                    or any related matter. This waiver applies to all claims, whether in contract, tort, or otherwise.</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.4 Strict Limitation Period</h4>
                    <p className="text-sm">Any claim, cause of action, or dispute arising out of or related to your use 
                    of the Service, these Terms, or any related matter MUST be filed within <strong>ONE (1) YEAR</strong> 
                    after such claim, cause of action, or dispute arose, regardless of when you discovered or should have 
                    discovered it. Claims filed after this period are permanently and forever barred. This limitation 
                    applies to all claims, regardless of type.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.5 Pre-Arbitration Dispute Resolution</h4>
                    <p className="text-sm">Before initiating arbitration, you must first attempt to resolve any dispute 
                    informally by contacting us through official channels. You must provide a written description of the 
                    dispute and attempted resolution. We will have 60 days to attempt informal resolution. If unresolved, 
                    you may proceed to arbitration.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.6 Costs & Fees</h4>
                    <p className="text-sm">Each party shall bear its own costs, fees, and expenses in any arbitration, 
                    including attorney's fees, unless otherwise determined by the arbitrator or required by applicable law.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">7.7 Confidentiality</h4>
                    <p className="text-sm">All arbitration proceedings, including the existence of the arbitration, 
                    all documents exchanged, and the outcome, shall be kept strictly confidential.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8 - Limitation of Liability */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">8</span>
              <Shield className="w-5 h-5 text-purple-400" />
              Limitation of Liability
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p className="font-semibold">TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>The Service is provided strictly "AS IS", "AS AVAILABLE", and "WITH ALL FAULTS" without warranties of any kind, either express or implied</li>
                <li>We EXPRESSLY DISCLAIM all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, accuracy, reliability, availability, security, and freedom from viruses or malware</li>
                <li>We are NOT liable for any direct, indirect, incidental, special, consequential, punitive, exemplary, or any other damages whatsoever</li>
                <li>We are NOT liable for lost profits, lost revenue, lost data, loss of cryptocurrency, loss of tokens, business interruption, personal injury, or any other losses</li>
                <li>Our TOTAL and MAXIMUM aggregate liability for any and all claims shall NOT exceed the lesser of: (a) the actual amount you directly paid us in the past 12 months (if any), or (b) $100 USD</li>
                <li>We are NOT liable for any failures, errors, or issues caused by third-party services, including but not limited to Pi Network, hosting providers, payment processors, or blockchain networks</li>
                <li>We are NOT liable for any damages arising from your misuse of the Service, violation of these Terms, or illegal activity</li>
                <li>We are NOT liable for Force Majeure events including natural disasters, war, terrorism, pandemic, government action, or any events beyond our reasonable control</li>
              </ul>
              <p className="font-semibold mt-4">
                Some jurisdictions do not allow the exclusion of certain warranties or limitations on liability. In such 
                jurisdictions, our liability shall be limited to the maximum extent permitted by applicable law. The 
                limitations in this section shall apply even if any limited remedy fails of its essential purpose.
              </p>
            </div>
          </section>

          {/* Section 9 - Indemnification */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">9</span>
              Comprehensive Indemnification
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                You agree to fully and unconditionally indemnify, defend, and hold completely harmless Arthereum Wallet, 
                its operators, developers, founders, owners, shareholders, affiliates, subsidiaries, parent companies, 
                officers, directors, employees, contractors, agents, partners, licensors, successors, and assigns 
                (collectively, "Indemnified Parties") from and against any and all claims, demands, actions, suits, 
                proceedings, damages, obligations, losses, liabilities, costs, debts, penalties, fines, and expenses 
                (including but not limited to reasonable attorneys' fees, legal costs, and expert witness fees) arising 
                from or related to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Your access to, use of, or misuse of the Service</li>
                <li>Your violation of any provision of these Terms</li>
                <li>Your violation of any rights of any third party, including intellectual property rights, privacy rights, or contractual rights</li>
                <li>Your violation of any applicable laws, regulations, rules, or orders in any jurisdiction</li>
                <li>Any content, data, or information you submit, post, transmit, or make available through the Service</li>
                <li>Any claims related to your cryptocurrency transactions, including tax obligations</li>
                <li>Any disputes between you and other users of the Service</li>
                <li>Your negligence, willful misconduct, or fraudulent activity</li>
                <li>Any regulatory or governmental investigation or action related to your use of the Service</li>
                <li>Any claims that your use of the Service caused damage to a third party</li>
              </ul>
              <p className="mt-3">
                This indemnification obligation shall survive the termination of these Terms and your use of the Service. 
                We reserve the right, at your expense, to assume the exclusive defense and control of any matter subject 
                to indemnification, and you agree to cooperate fully with our defense.
              </p>
            </div>
          </section>

          {/* Section 10 - Account Termination */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">10</span>
              <UserX className="w-5 h-5 text-purple-400" />
              Account Suspension & Termination
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>We reserve the absolute right to suspend, restrict, or terminate your account and access to the Service 
              at any time, for any reason or no reason, with or without notice, at our sole discretion. Reasons may include, 
              but are not limited to:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Violation or suspected violation of any provision of these Terms</li>
                <li>Fraudulent, deceptive, illegal, or abusive activity</li>
                <li>Creating multiple accounts for abuse, manipulation, or circumvention purposes</li>
                <li>Attempting to manipulate, exploit, hack, or abuse the Service or its features</li>
                <li>Providing false, inaccurate, or misleading information</li>
                <li>Engaging in any activity we deem harmful to the Service, other users, or our reputation</li>
                <li>Inactivity for an extended period</li>
                <li>Legal, regulatory, or compliance requirements</li>
                <li>Business, technical, or operational reasons</li>
                <li>At our discretion for any other reason</li>
              </ul>
              <p className="mt-3">
                <strong>10.1 Effect of Termination:</strong> Upon termination, you immediately lose access to your account 
                and any and all tokens, balances, rewards, data, or other assets associated with it. All such assets may 
                be permanently forfeited. We are under NO obligation to provide refunds, compensation, or return of any 
                assets upon account termination.
              </p>
              <p>
                <strong>10.2 Survival:</strong> The provisions of these Terms that by their nature should survive termination 
                shall survive, including but not limited to ownership provisions, warranty disclaimers, limitation of liability, 
                indemnification, and dispute resolution provisions.
              </p>
            </div>
          </section>

          {/* Section 11 - Intellectual Property */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">11</span>
              Intellectual Property Rights
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                <strong>11.1 Ownership:</strong> The Service, including all content, features, functionality, software, code, 
                design, text, graphics, logos, icons, images, and all intellectual property rights therein, are owned by us 
                or our licensors and are protected by intellectual property laws. All rights not expressly granted are reserved.
              </p>
              <p>
                <strong>11.2 Limited License:</strong> Subject to your compliance with these Terms, we grant you a limited, 
                non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial 
                purposes only. This license does not include any right to resell, distribute, copy, modify, or create 
                derivative works.
              </p>
              <p>
                <strong>11.3 Prohibited Uses:</strong> You may not copy, modify, distribute, sell, lease, reverse engineer, 
                decompile, disassemble, or create derivative works based on the Service without our prior written consent.
              </p>
              <p>
                <strong>11.4 Trademarks:</strong> "Arthereum", "ATH", and associated logos are trademarks of the Service 
                operators. You may not use these marks without prior written permission.
              </p>
            </div>
          </section>

          {/* Section 12 - User Conduct */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">12</span>
              User Conduct & Prohibited Activities
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>You agree NOT to engage in any of the following prohibited activities:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Using the Service for any illegal purpose or in violation of any laws</li>
                <li>Attempting to gain unauthorized access to the Service, other accounts, or systems</li>
                <li>Using bots, scripts, automation, or similar technology to interact with the Service</li>
                <li>Manipulating or attempting to manipulate any feature of the Service</li>
                <li>Engaging in market manipulation, wash trading, or fraudulent trading activity</li>
                <li>Circumventing or attempting to circumvent any security measures</li>
                <li>Transmitting malware, viruses, or other harmful code</li>
                <li>Harassing, threatening, or abusing other users</li>
                <li>Impersonating any person or entity</li>
                <li>Collecting or harvesting user data without consent</li>
                <li>Using the Service to launder money or finance illegal activity</li>
                <li>Violating any third-party rights</li>
              </ul>
            </div>
          </section>

          {/* Section 13 - Modifications to Service */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">13</span>
              Modifications & Discontinuation
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                We reserve the absolute and unrestricted right to modify, suspend, discontinue, or terminate any aspect 
                of the Service at any time, for any reason, with or without prior notice. This includes, but is not 
                limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Changing grant amounts, frequencies, rates, or eligibility criteria</li>
                <li>Modifying staking terms, contribution requirements, boost calculations, or period durations</li>
                <li>Adjusting contest rules, prizes, odds, or participation requirements</li>
                <li>Updating subscription prices, features, or benefits</li>
                <li>Adding, removing, or modifying any features or functionality</li>
                <li>Changing token economics, supply, or distribution mechanisms</li>
                <li>Discontinuing the Service entirely</li>
              </ul>
              <p className="mt-3">
                We shall NOT be liable to you or any third party for any modification, suspension, or discontinuation 
                of the Service. You have no right to continued access, specific features, or compensation for changes.
              </p>
            </div>
          </section>

          {/* Section 14 - Privacy */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">14</span>
              <Lock className="w-5 h-5 text-purple-400" />
              Privacy & Data
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms 
                by reference. By using the Service, you consent to the collection, use, and sharing of your information 
                as described in the Privacy Policy.
              </p>
              <p>
                You acknowledge that blockchain transactions are public and transparent. Transaction data, including 
                wallet addresses and amounts, may be publicly visible on the blockchain and cannot be deleted.
              </p>
            </div>
          </section>

          {/* Section 15 - Governing Law */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">15</span>
              <Globe className="w-5 h-5 text-purple-400" />
              Governing Law & Jurisdiction
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which 
                the Service operators are located, without regard to conflict of law principles.
              </p>
              <p>
                For any matters not subject to arbitration, you agree to submit to the exclusive personal jurisdiction 
                of the courts in that jurisdiction. You waive any objection to venue or forum non conveniens.
              </p>
              <p>
                If any provision of these Terms is found to conflict with applicable law, that provision shall be 
                interpreted to reflect the original intent as closely as possible while complying with law.
              </p>
            </div>
          </section>

          {/* Section 16 - Miscellaneous */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">16</span>
              General Provisions
            </h2>
            <div className="text-slate-300 leading-relaxed space-y-3 pl-11">
              <p>
                <strong>16.1 Severability:</strong> If any provision of these Terms is held to be invalid, illegal, or 
                unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision 
                shall be modified to the minimum extent necessary to make it valid and enforceable while preserving its 
                original intent.
              </p>
              <p>
                <strong>16.2 Entire Agreement:</strong> These Terms, together with the Privacy Policy and any other 
                policies referenced herein, constitute the entire agreement between you and us regarding the Service. 
                These Terms supersede all prior agreements, representations, warranties, and understandings.
              </p>
              <p>
                <strong>16.3 Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not 
                constitute a waiver of such right or provision. Any waiver must be in writing and signed by us.
              </p>
              <p>
                <strong>16.4 Assignment:</strong> You may not assign or transfer these Terms or your rights hereunder 
                without our prior written consent. We may assign these Terms without restriction.
              </p>
              <p>
                <strong>16.5 No Third-Party Beneficiaries:</strong> These Terms do not create any third-party beneficiary 
                rights except as expressly provided.
              </p>
              <p>
                <strong>16.6 Headings:</strong> Section headings are for convenience only and do not affect interpretation.
              </p>
              <p>
                <strong>16.7 Language:</strong> In case of discrepancy between translations, the English version prevails.
              </p>
            </div>
          </section>

          {/* Section 17 - Contact */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400 text-sm font-bold">17</span>
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Contact Information
            </h2>
            <div className="text-slate-300 leading-relaxed pl-11">
              <p>
                If you have any questions about these Terms, please contact us through the official channels provided 
                within the Arthereum Wallet application. We will endeavor to respond to inquiries within a reasonable 
                timeframe, but are under no obligation to do so.
              </p>
            </div>
          </section>

          {/* Final Acknowledgment */}
          <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-purple-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-purple-300 mb-2">Final Acknowledgment & Agreement</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  BY ACCESSING, USING, OR CONTINUING TO USE ARTHEREUM WALLET, YOU HEREBY ACKNOWLEDGE, CONFIRM, AND AGREE THAT:
                  (1) YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE IN THEIR ENTIRETY;
                  (2) THESE TERMS CONSTITUTE A LEGALLY BINDING AGREEMENT BETWEEN YOU AND THE OPERATORS OF THIS SERVICE;
                  (3) YOU HAVE THE LEGAL CAPACITY TO ENTER INTO THIS AGREEMENT;
                  (4) YOU UNDERSTAND AND ACCEPT ALL RISKS ASSOCIATED WITH USING THIS SERVICE, INCLUDING THE RISK OF COMPLETE 
                  FINANCIAL LOSS;
                  (5) YOU WAIVE YOUR RIGHT TO PARTICIPATE IN CLASS ACTIONS AND AGREE TO BINDING ARBITRATION;
                  (6) YOU UNDERSTAND THAT PI CONTRIBUTIONS ARE PERMANENT, IRREVERSIBLE, AND NON-REFUNDABLE;
                  (7) ATH TOKENS HAVE NO GUARANTEED VALUE AND SHOULD NOT BE EXPECTED TO PROVIDE FINANCIAL RETURNS.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
